import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import Alert from 'react-s-alert';
import { withNamespaces } from 'react-i18next';
import { VoxelDialog } from '@wikifactory/voxel-ui';
import { AiOutlinePlus } from 'react-icons/ai';
import { isNil } from 'lodash';

import { FormWrapper, FormDivider } from 'libs/forms/styles';
import { FullWidthDivider } from 'legacyComponents/styled/lib';
import Paragraph from 'components/Paragraph';
import { Button } from 'components';

import {
  SeatsFormItem,
  SeatsFormItemTitle,
  SeatsFormItemValue,
  SeatInput,
} from 'apps/payment/styles';
import { formatAmount, getBillingCycleByPlanName } from 'utils/formatStrings';
import { MAX_ADD_SEATS } from 'constants/subscription';

const BillingCycleMap: Record<string, string> = {
  yearly: 'Annually',
  monthly: 'Monthly',
};

export const AddSeatsModal = withNamespaces()(
  observer(
    ({
      t,
      paymentSpace,
      paymentSpacePreview,
      addSeatsPreviewResult,
      loadingPreview,
      open,
      onClose,
    }: any) => {
      const { subscription } = paymentSpace;

      const { seats, plan } = subscription || {};

      const billingCycle = plan?.name
        ? BillingCycleMap[getBillingCycleByPlanName(plan.name)]
        : '';

      const {
        addSeatsAmount,
        freeSeats = 0,
        includedPlanSeats = 0,
      } = seats || {};

      const { totalAmount, perSeatAmount } = addSeatsPreviewResult || {};

      const preview = paymentSpacePreview && paymentSpacePreview.preview;
      const displayPreview = addSeatsAmount && preview;

      const currentCostTotal = subscription?.displayNextAmount;

      const newTotalSeats = displayPreview
        ? preview.seats?.totalSubSeats
        : seats?.newTotalSeats;

      const preSeatsAmount = perSeatAmount || 0;
      const displayPerSeatAmount = perSeatAmount
        ? formatAmount(preSeatsAmount / 100)
        : '-';

      const totalFreeSeats = freeSeats + includedPlanSeats;

      const newCostTotal = isNil(totalAmount)
        ? '-'
        : formatAmount(totalAmount / 100);

      const confirmText = useMemo(() => {
        if (loadingPreview) return 'Loading Preview';

        return 'Save';
      }, [loadingPreview, seats?.loading]);

      useEffect(() => {
        if (open) {
          seats?.Update('add', 0);
        }
      }, [open]);

      const loading = seats?.loading || loadingPreview;
      const disableSave = !seats?.addSeatsAmount || seats?.addSeatsAmount <= 0;

      const saveButton = (
        <Button
          size='small'
          data-testid='save-add-seats'
          text={confirmText}
          icon={loading ? 'locked' : null}
          disabled={disableSave || loading}
          outline
          loading={loading}
          onClick={() => {
            seats?.ConfirmAddSeats({
              successCallback: onClose,
            });
          }}
        />
      );

      const handleAddSeats = (seatNumber = 0) => {
        if (seatNumber > MAX_ADD_SEATS) {
          Alert.error(
            t(`No more than ${MAX_ADD_SEATS} seats can be added at a time.`),
          );
          return;
        }

        seats.Update('add', seatNumber);
      };

      return (
        <VoxelDialog
          title={
            <>
              {t('Adding Seats')}
              <FormDivider />
            </>
          }
          style={{
            // Promise level more than subheader component zIndex.
            zIndex: 1000000,
          }}
          disableEscapeKeyDown
          open={open}
          wrapClassName='pt-0'
          PaperProps={{ sx: { width: 800 } }}
          titleProps={{ className: 'text-left font-extrabold text-24 ml-4' }}
          actions={
            <>
              <Button
                size='small'
                variant='transparent'
                data-testid='cancel-add-seats'
                text='Cancel'
                className='border'
                disabled={loading}
                onClick={() => {
                  onClose();
                  seats?.SetFormVisibility(null);
                }}
              />
              {disableSave ? (
                <div className='cursor-not-allowed ml-4'>{saveButton}</div>
              ) : (
                saveButton
              )}
            </>
          }
        >
          <FormWrapper posting={loadingPreview} noPadding responsive>
            <SeatsFormItem>
              <SeatsFormItemTitle>
                <Paragraph>{t('Current Seats')}</Paragraph>
              </SeatsFormItemTitle>
              <SeatsFormItemValue>
                <Paragraph data-testid='billingSettingsPage-currentSeats-text'>
                  {seats?.totalSubSeats}
                </Paragraph>
              </SeatsFormItemValue>
            </SeatsFormItem>
            <SeatsFormItem>
              <SeatsFormItemTitle>
                <Paragraph>{t('Add Seats')}</Paragraph>
              </SeatsFormItemTitle>
              <SeatsFormItemValue>
                <div className='flex items-center justify-end'>
                  <span
                    className='cursor-pointer'
                    onClick={() => {
                      handleAddSeats((addSeatsAmount ?? 0) + 1);
                    }}
                  >
                    <AiOutlinePlus size={16} />
                  </span>
                  <SeatInput
                    className='h-9 ml-1 w-20'
                    autoFocus
                    value={addSeatsAmount ?? 0}
                    data-testid='billingSettingsPage-addSeats-input'
                    onChange={e => {
                      handleAddSeats(e.target.value as unknown as number);
                    }}
                  />
                </div>
              </SeatsFormItemValue>
            </SeatsFormItem>
            <SeatsFormItem>
              <SeatsFormItemTitle>
                <Paragraph>
                  <strong>{t('New Total Seats')}</strong>
                </Paragraph>
              </SeatsFormItemTitle>
              <SeatsFormItemValue>
                <Paragraph data-testid='billingSettingsPage-newTotalSeats-text'>
                  {newTotalSeats || '-'}
                </Paragraph>
              </SeatsFormItemValue>
            </SeatsFormItem>
            <FullWidthDivider margin='0px 0px 20px 0px' />

            <SeatsFormItem>
              <SeatsFormItemTitle>
                <Paragraph>{t('Cost Per Seat')}</Paragraph>
              </SeatsFormItemTitle>
              <SeatsFormItemValue>
                <Paragraph data-testid='billingSettingsPage-costPerSeats-text'>
                  {preview ? displayPerSeatAmount : '-'}
                </Paragraph>
              </SeatsFormItemValue>
            </SeatsFormItem>
            <SeatsFormItem>
              <SeatsFormItemTitle>
                <Paragraph>{t('Free Seats')}</Paragraph>
              </SeatsFormItemTitle>
              <SeatsFormItemValue>
                <Paragraph data-testid='billingSettingsPage-freeSeats-text'>
                  {isNil(totalFreeSeats) ? '-' : totalFreeSeats}
                </Paragraph>
              </SeatsFormItemValue>
            </SeatsFormItem>
            <FullWidthDivider margin='0px 0px 20px 0px' />

            <SeatsFormItem>
              <SeatsFormItemTitle data-testid='billingSettingsPage-currentBillingCycle-text'>
                <Paragraph>{t(`Current ${billingCycle} Total`)}</Paragraph>
              </SeatsFormItemTitle>
              <SeatsFormItemValue>
                <Paragraph data-testid='billingSettingsPage-currentCostTotal-text'>
                  {currentCostTotal || '-'}
                </Paragraph>
              </SeatsFormItemValue>
            </SeatsFormItem>
            <SeatsFormItem>
              <SeatsFormItemTitle>
                <Paragraph data-testid='billingSettingsPage-newBillingCycle-text'>
                  <strong>{t(`New ${billingCycle} Total`)}</strong>
                </Paragraph>
              </SeatsFormItemTitle>
              <SeatsFormItemValue>
                <Paragraph data-testid='billingSettingsPage-newCostTotal-text'>
                  <strong>{newCostTotal || '-'}</strong>
                </Paragraph>
              </SeatsFormItemValue>
            </SeatsFormItem>
            <FullWidthDivider margin='0px 0px 20px 0px' />
          </FormWrapper>
        </VoxelDialog>
      );
    },
  ),
);
